﻿/**
 * The outcome of a popup window interaction.
 */
export enum PopupResult {
    /**
     * The popup was redirected to the return url, indicating completion.
     */
    Completed = "Completed",

    /**
     * The popup was closed by the user before reaching the return url.
     */
    Aborted = "Aborted",
}

/**
 * Class that opens a url (e.g. an Entra ID consent page) in a popup window and
 * waits until either the popup navigates to a given return url (completed) or
 * the popup is closed by the user (aborted).
 */
export class PopupWindow {
    private readonly pollIntervalMs: number;

    public constructor(pollIntervalMs: number = 500) {
        this.pollIntervalMs = pollIntervalMs;
    }

    /**
    * Opens the given url in a popup window and waits until the popup either
    * navigates to the returnUrl (Completed) or is closed by the user (Aborted).
    */
    public async OpenAndWaitAsync(url: string, returnUrl: string): Promise<PopupResult> {
        const width = 600;
        const height = 750;
        const left = Math.max(0, (window.screen.width - width) / 2);
        const top = Math.max(0, (window.screen.height - height) / 2);

        const urlWithRedirectUri = this.EnsureRedirectUri(url, returnUrl);

        const popup = window.open(
            urlWithRedirectUri,
            "_blank",
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
        );

        if (popup === null || popup === undefined) {
            return PopupResult.Aborted;
        }

        popup.focus();

        const returnUrlNormalized = this.NormalizeUrl(returnUrl);

        return new Promise<PopupResult>((resolve): void => {
            const intervalId = window.setInterval((): void => {
                if (popup.closed) {
                    window.clearInterval(intervalId);
                    resolve(PopupResult.Aborted);
                    return;
                }

                let currentUrl: string | null = null;
                try {
                    // Throws while the popup is on a cross-origin page (e.g. Entra ID).
                    currentUrl = popup.location.href;
                } catch {
                    // Cross-origin access blocked; popup has not returned yet.
                    currentUrl = null;
                }

                if (currentUrl !== null && this.NormalizeUrl(currentUrl) === returnUrlNormalized) {
                    window.clearInterval(intervalId);
                    popup.close();
                    resolve(PopupResult.Completed);
                }
            }, this.pollIntervalMs);
        });
    }

    private EnsureRedirectUri(url: string, returnUrl: string): string {
        try {
            const parsed = new URL(url, window.location.href);
            if (!parsed.searchParams.has("redirect_uri")) {
                const redirectUri = new URL(returnUrl, window.location.href).toString();
                parsed.searchParams.set("redirect_uri", redirectUri);
            }

            return parsed.toString();
        } catch {
            return url;
        }
    }

    private NormalizeUrl(url: string): string {
        try {
            const parsed = new URL(url, window.location.href);
            return `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, "");
        } catch {
            return url.replace(/\/+$/, "");
        }
    }
}

window['InitPopupWindow'] = function (pollIntervalMs?: number) {
    return new PopupWindow(pollIntervalMs);
};